import useCurrentlyPlayingSong from "@hooks/useCurrentlyPlayingSong";
import useSpotifyTracks from "@hooks/useSpotifyTracks";
import BoltIcon from "@mui/icons-material/Bolt";
import CoverIcon from "@mui/icons-material/ContentCopy";
import FavoriteIcon from "@mui/icons-material/Favorite";
import NonFavoriteIcon from "@mui/icons-material/FavoriteBorder";
import TrendingIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import { TracksMap } from "@pages/LyricsView/LyricToolbar";
import React, { useCallback, useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useTrackRecommendations } from "../context/TrackRecommendationsContext";

const LONG_PRESS_MS = 500;

interface TrackListItemProps {
  track: Track;
  onPlay: () => void;
  onQueue: (trackId: string, trackName: string) => void;
}

const TrackListItem: React.FC<TrackListItemProps> = ({ track, onPlay, onQueue }) => {
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      longPressTriggeredRef.current = false;
      try {
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      } catch {
        /* setPointerCapture may not be supported in some environments */
      }
      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null;
        longPressTriggeredRef.current = true;
        onQueue(track.id, track.name);
      }, LONG_PRESS_MS);
    },
    [onQueue, track.id, track.name]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      clearLongPressTimer();
      if (!longPressTriggeredRef.current) {
        onPlay();
      }
    },
    [onPlay, clearLongPressTimer]
  );

  const handlePointerCancel = useCallback(() => {
    clearLongPressTimer();
  }, [clearLongPressTimer]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  return (
    <ListItem
      button
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onPointerLeave={handlePointerCancel}
      onContextMenu={handleContextMenu}
      sx={{ userSelect: "none", touchAction: "manipulation" }}
    >
      <ListItemIcon>
        {track.liked && <FavoriteIcon />}
        {!track.liked && <NonFavoriteIcon />}
      </ListItemIcon>
      <img
        src={track.coverUrl}
        alt={track.name}
        style={{ width: 50, height: 50, marginRight: 16 }}
      />
      <ListItemText primary={track.name} secondary={track.artists.join(", ")} />
      <Box textAlign="center">
        <Typography variant="caption" display="block">
          Popularity
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {track.popularity}
        </Typography>
      </Box>
    </ListItem>
  );
};

interface Track {
  id: string;
  name: string;
  artists: string[];
  coverUrl: string;
  popularity: number;
  liked: boolean;
}

interface TrackSearchModalProps {
  open: boolean;
  onClose: () => void;
  onTrackSelect: (trackId: string) => void;
  onTrackQueue: (trackId: string, trackName: string) => void;
  tracksMap: TracksMap;
  setTracksMap: React.Dispatch<React.SetStateAction<TracksMap>>;
}

const TrackSearchModal: React.FC<TrackSearchModalProps> = ({
  open,
  onClose,
  onTrackSelect,
  onTrackQueue,
  tracksMap,
  setTracksMap
}) => {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [tab, setTab] = useState<number>(0);
  const { searchLiveTracks, searchCoverTracks, searchTopTracks } = useSpotifyTracks();
  const currentlyPlayingSong = useCurrentlyPlayingSong();
  const [visibleTracks, setVisibleTracks] = useState(5);
  const [hasLiveTracks, setHasLiveTracks] = useState(false);
  const [hasCoverTracks, setHasCoverTracks] = useState(false);

  // Use background track recommendations
  const trackRecommendations = useTrackRecommendations();

  useEffect(() => {
    if (!open) {
      return;
    }

    const fetchTracks = async () => {
      if (songChanged()) {
        resetState();
      }

      // Use background fetch data if available
      if (
        trackRecommendations.hasData &&
        trackRecommendations.currentSongId ===
          currentlyPlayingSong?.currentlyPlayingObject?.item?.id
      ) {
        const backgroundTracks = trackRecommendations.tracks;
        if (backgroundTracks && backgroundTracks.length > 0) {
          const hasLive = backgroundTracks[1]?.length > 0;
          const hasCover = backgroundTracks[2]?.length > 0;
          setHasLiveTracks(hasLive);
          setHasCoverTracks(hasCover);
          const sources = [0, ...(hasLive ? [1] : []), ...(hasCover ? [2] : [])];
          setTracks(backgroundTracks[sources[tab]] ?? backgroundTracks[0]);

          setTracksMap({
            songId: currentlyPlayingSong.currentlyPlayingObject.item.id,
            tracks: backgroundTracks
          });
          return;
        }
      }

      // Fallback to manual fetching if background data not available
      const cachedTracks = tracksMap.tracks;
      if (cachedTracks && cachedTracks.length > 0) {
        const hasLive = cachedTracks[1]?.length > 0;
        const hasCover = cachedTracks[2]?.length > 0;
        setHasLiveTracks(hasLive);
        setHasCoverTracks(hasCover);
        const sources = [0, ...(hasLive ? [1] : []), ...(hasCover ? [2] : [])];
        setTracks(cachedTracks[sources[tab]] ?? cachedTracks[0]);
        return;
      }

      if (
        open &&
        currentlyPlayingSong &&
        currentlyPlayingSong.currentlyPlayingObject &&
        currentlyPlayingSong.currentlyPlayingObject.item
      ) {
        setLoading(true);
        try {
          const artist = currentlyPlayingSong.currentlyPlayingObject?.item?.artists
            .map(a => a.name)
            .join(", ");

          const topTracks = await searchTopTracks(
            currentlyPlayingSong.currentlyPlayingObject.item?.name,
            artist
          );
          const liveTracks = await searchLiveTracks(
            currentlyPlayingSong.currentlyPlayingObject.item?.name,
            artist
          );
          const coverTracks = await searchCoverTracks(
            currentlyPlayingSong.currentlyPlayingObject.item?.name,
            artist
          );
          const hasLive = liveTracks.length > 0;
          const hasCover = coverTracks.length > 0;
          setHasLiveTracks(hasLive);
          setHasCoverTracks(hasCover);
          const allTracks = [topTracks, liveTracks, coverTracks];
          setTracksMap({
            songId: currentlyPlayingSong.currentlyPlayingObject.item.id,
            tracks: allTracks
          });
          const sources = [0, ...(hasLive ? [1] : []), ...(hasCover ? [2] : [])];
          setTracks(allTracks[sources[tab]] ?? topTracks);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchTracks();
  }, [open, currentlyPlayingSong, setTracksMap, tab, trackRecommendations]);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
    setTracks([]);
  };

  // Map tab index to tracks array index: 0=Top, 1=Live (if present), 2=Covers (if present)
  // Exclude Live and Covers when their lists are empty (same behavior for both)
  const tabSources = [0, ...(hasLiveTracks ? [1] : []), ...(hasCoverTracks ? [2] : [])];
  const currentTracksSourceIndex = tabSources[tab] ?? 0;
  const displayedTracks: Track[] = tracksMap.tracks?.[currentTracksSourceIndex] ?? tracks ?? [];

  const fetchMoreData = () => {
    const trackCount = Array.isArray(displayedTracks) ? displayedTracks.length : 0;
    if (visibleTracks < trackCount) {
      setVisibleTracks(prevVisibleTracks => prevVisibleTracks + 5);
    }
  };

  function closeModal(): void {
    // setTracks([]);
    onClose();
  }

  const songChanged = () => {
    return (
      !currentlyPlayingSong.currentlyPlayingObject?.item?.id ||
      tracksMap.songId !== currentlyPlayingSong.currentlyPlayingObject?.item?.id
    );
  };

  function resetState() {
    setTracks([]);
    setTracksMap({ tracks: [] });
    setHasCoverTracks(false);
    setHasLiveTracks(false);
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        component="div"
        role="presentation"
        sx={{
          width: 400,
          maxHeight: "60vh",
          bgcolor: "background.paper",
          p: 0,
          left: "0",
          bottom: 76,
          position: "absolute",
          margin: "auto",
          overflow: "hidden"
        }}
      >
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{
            pl: 5
          }}
        >
          Recommendations
        </Typography>
        <Tabs value={tab} onChange={handleTabChange} centered>
          <Tab icon={<TrendingIcon />} label="Top Songs" />
          {hasLiveTracks && <Tab icon={<BoltIcon />} label="Live" />}
          {hasCoverTracks && <Tab icon={<CoverIcon />} label="Covers" />}
        </Tabs>
        {loading && displayedTracks.length !== 0 ? (
          <CircularProgress />
        ) : !displayedTracks || displayedTracks.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ mt: 2 }}>
            {trackRecommendations.isFetching ? "Loading recommendations..." : "No Songs found"}
          </Typography>
        ) : (
          <InfiniteScroll
            dataLength={visibleTracks}
            next={fetchMoreData}
            height={400}
            hasMore={visibleTracks < displayedTracks.length}
            loader={<CircularProgress />}
            pullDownToRefreshThreshold={50}
          >
            <List>
              {displayedTracks.slice(0, visibleTracks).map(track => (
                <TrackListItem
                  key={track.id}
                  track={track}
                  onPlay={() => onTrackSelect(track.id)}
                  onQueue={onTrackQueue}
                />
              ))}
            </List>
          </InfiniteScroll>
        )}
        <Button onClick={closeModal}>Cancel</Button>
      </Box>
    </Modal>
  );
};

export default TrackSearchModal;
